import React, { useCallback, useEffect, useRef, useMemo, useState } from "react";
import "./Sales.css";
import SidePanel from "./SidePanel";
import { apiFetch } from "../utils/api";
import { canDo } from "../utils/permissions";
import Paginator from "./Paginator";

const escapeHtml = str =>
  String(str == null ? "" : str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;");

const COMPANY_INFO = {
  address: "Dubai, United Arab Emirates",
  trn: "100123456700003",
  phone: "+971 00 000 0000"
};

const defaultForm = {
  invoiceNo: "",
  saleDate: new Date().toISOString().slice(0, 10),
  branchPublicId: "",
  customerPublicId: "",
  lineItems: [],
  vatPercent: "5",
  paymentMethod: "Cash",
  note: ""
};

const defaultLineDraft = {
  servicePublicId: "",
  memberPublicId: "",
  servicePrice: "",
  lineDiscountValue: ""
};

const monthLabels = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const paymentOptions = ["Cash", "Card", "Online", "Mixed"];

const getTodayDateKey = () => {
  const now = new Date();
  const yearPart = now.getFullYear();
  const monthPart = String(now.getMonth() + 1).padStart(2, "0");
  const dayPart = String(now.getDate()).padStart(2, "0");
  return `${yearPart}-${monthPart}-${dayPart}`;
};

const getCurrentMonthValue = () => String(new Date().getMonth() + 1);
const getCurrentYearValue = () => String(new Date().getFullYear());
const parseDateFilterKey = value => {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
};

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

const buildDateFilterKey = (year, month, day) => {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return "";
  if (y < 1 || m < 1 || m > 12) return "";
  const maxDay = getDaysInMonth(y, m);
  const clampedDay = Math.min(Math.max(d, 1), maxDay);
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(clampedDay).padStart(2, "0")}`;
};

const Sales = ({ currentUser, pendingConversion, onConversionDone, onAppointmentConverted }) => {
  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(() => getTodayDateKey());
  const [monthFilter, setMonthFilter] = useState(() => getCurrentMonthValue());
  const [yearFilter, setYearFilter] = useState(() => getCurrentYearValue());
  const [paymentFilter, setPaymentFilter] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingSaleRef, setEditingSaleRef] = useState("");
  const [panelReadOnly, setPanelReadOnly] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saleAudit, setSaleAudit] = useState(null);
  const [salesPage, setSalesPage] = useState(1);
  const [salesPageSize, setSalesPageSize] = useState(25);
  const [form, setForm] = useState(defaultForm);
  const [lineDraft, setLineDraft] = useState(() => ({ ...defaultLineDraft }));
  const [manualCustomerName, setManualCustomerName] = useState("");
  const [manualCustomerContact, setManualCustomerContact] = useState("");
  const [manualCustomerBirthday, setManualCustomerBirthday] = useState("");
  const [customerFocused, setCustomerFocused] = useState(false);
  const [serviceSearchTerm, setServiceSearchTerm] = useState("");
  const [serviceFocused, setServiceFocused] = useState(false);

  const isEditing = !!editingSaleRef;
  const canEditSales = canDo(currentUser, "sales", "edit");
  const submitInFlightRef = useRef(false);
  const defaultAllowedBranchPublicId = Array.isArray(currentUser?.allowedBranchPublicIds) && currentUser.allowedBranchPublicIds.length === 1
    ? String(currentUser.allowedBranchPublicIds[0] || "")
    : "";

  const getSaleRef = sale => String(sale?.publicId || sale?.id || "");

  const createLineId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const getLineDiscountValue = item => {
    const price = Math.max(0, Number(item?.servicePrice || 0));
    const discount = Math.max(0, Number(item?.lineDiscountValue || 0));
    return Math.min(discount, price);
  };

  const getLineTotal = item => Math.max(0, Number(item?.servicePrice || 0) - getLineDiscountValue(item));

  const buildEditableLineItems = (sourceServices, legacyDiscountAmount = 0) => {
    const baseItems = sourceServices.map(item => ({
      lineId: createLineId(),
      servicePublicId: item.servicePublicId || "",
      serviceName: item.name || "Service",
      servicePrice: Number(item.price || 0),
      lineDiscountValue: Number(item.lineDiscountAmount || 0),
      memberPublicId: item.memberPublicId || "",
      memberName: item.memberName || "Team Member"
    }));

    const existingLineDiscount = baseItems.reduce((sum, item) => sum + getLineDiscountValue(item), 0);
    const remainingLegacyDiscount = Math.max(0, Number(legacyDiscountAmount || 0) - existingLineDiscount);
    if (remainingLegacyDiscount <= 0 || baseItems.length === 0) {
      return baseItems;
    }

    const subtotalBeforeDiscount = baseItems.reduce((sum, item) => sum + Number(item.servicePrice || 0), 0);
    if (subtotalBeforeDiscount <= 0) {
      return baseItems;
    }

    let allocated = 0;
    return baseItems.map((item, index) => {
      const price = Math.max(0, Number(item.servicePrice || 0));
      const currentDiscount = getLineDiscountValue(item);
      const maxAdditionalDiscount = Math.max(0, price - currentDiscount);
      const proportionalShare = index === baseItems.length - 1
        ? remainingLegacyDiscount - allocated
        : (remainingLegacyDiscount * price) / subtotalBeforeDiscount;
      const appliedShare = Math.min(maxAdditionalDiscount, Math.max(0, proportionalShare));
      allocated += appliedShare;
      return {
        ...item,
        lineDiscountValue: currentDiscount + appliedShare
      };
    });
  };

  const normalizeMobile = value => String(value || "").replace(/[\s()-]/g, "").toLowerCase();

  const splitCustomerName = name => {
    const normalized = String(name || "").trim().replace(/\s+/g, " ");
    if (!normalized) return { firstName: "", lastName: "" };
    const [firstName, ...rest] = normalized.split(" ");
    return { firstName: firstName || "", lastName: rest.join(" ") };
  };

  const getClientDisplayName = client => `${client?.firstName || ""} ${client?.lastName || ""}`.trim() || "Customer";

  const loadData = useCallback(() => {
    
   
    apiFetch("/api/clients")
      .then(res => res.json())
      .then(data => setClients(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to load clients", err));

    apiFetch("/api/services")
      .then(res => res.json())
      .then(data => setServices(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to load services", err));

    apiFetch("/api/team-members")
      .then(res => res.json())
      .then(data => setTeamMembers(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to load team members", err));

    apiFetch("/api/branches")
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setBranches(list);
        if (list.length === 1 && list[0]?.publicId) {
          setBranchFilter(String(list[0].publicId));
        }
        if (!branchFilter && defaultAllowedBranchPublicId) {
          setForm(prev => ({ ...prev, branchPublicId: prev.branchPublicId || defaultAllowedBranchPublicId }));
        } else if (!branchFilter && list[0]?.publicId) {
          setForm(prev => ({ ...prev, branchPublicId: prev.branchPublicId || list[0].publicId }));
        }
      })
      .catch(err => {
        console.error("Failed to load branches", err);
        setBranches([]);
      });
      const salesPath = branchFilter ? `/api/sales?branchPublicId=${encodeURIComponent(branchFilter)}` : "/api/sales";
       apiFetch(salesPath)
      .then(res => res.json())
      .then(data => setSales(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to load sales", err));

  }, [branchFilter, defaultAllowedBranchPublicId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openPanel = () => {
    setEditingSaleRef("");
    setPanelReadOnly(false);
    setForm({
      ...defaultForm,
      branchPublicId: branchFilter || defaultAllowedBranchPublicId || branches[0]?.publicId || ""
    });
    setManualCustomerName("");
    setManualCustomerContact("");
    setManualCustomerBirthday("");
    setLineDraft({ ...defaultLineDraft });
    setServiceSearchTerm("");
    setServiceFocused(false);
    setSaleAudit(null);
    setPanelOpen(true);
  };

  const openEditPanel = (sale, options = {}) => {
    const shouldReadOnly = !!options.readOnly;
    const matchedClient = clients.find(client => (
      String(client.publicId || "") === String(sale.customerPublicId || "")
      || `${client.firstName || ""} ${client.lastName || ""}`.trim().toLowerCase() === String(sale.customerName || "").trim().toLowerCase()
    ));
    const sourceServices = Array.isArray(sale.services) ? sale.services : [];
    const editCustomerPublicId = sale.customerPublicId || matchedClient?.publicId || "";

    setEditingSaleRef(getSaleRef(sale));
    setPanelReadOnly(shouldReadOnly);
    setForm({
      invoiceNo: sale.invoiceNo || "",
      saleDate: sale.saleDate || new Date().toISOString().slice(0, 10),
      branchPublicId: sale.branchPublicId || branches[0]?.publicId || "",
      customerPublicId: editCustomerPublicId,
      lineItems: buildEditableLineItems(sourceServices, sale.discountAmount),
      vatPercent: String(Number(sale.vatPercent || 5)),
      paymentMethod: sale.paymentMethod || "Cash",
      note: sale.note || ""
    });
    setManualCustomerName(sale.customerName || "");
    setManualCustomerContact(matchedClient?.mobile || "");
    setManualCustomerBirthday(matchedClient?.birthday || "");
    setLineDraft({ ...defaultLineDraft });
    setServiceSearchTerm("");
    setServiceFocused(false);
    setSaleAudit({ createdBy: sale.createdBy || "", createdAt: sale.createdAt || "", updatedBy: sale.updatedBy || "", updatedAt: sale.updatedAt || "" });
    setPanelOpen(true);
  };

  const closePanel = () => {
    conversionSourceRef.current = null;
    submitInFlightRef.current = false;
    setIsSaving(false);
    setSaleAudit(null);
    setPanelOpen(false);
    setEditingSaleRef("");
    setPanelReadOnly(false);
    setForm(defaultForm);
    setManualCustomerName("");
    setManualCustomerContact("");
    setManualCustomerBirthday("");
    setLineDraft({ ...defaultLineDraft });
    setServiceSearchTerm("");
    setServiceFocused(false);
  };

  // Auto-prefill from appointment conversion
  const conversionAppliedRef = useRef(false);
  const conversionSourceRef = useRef(null);
  useEffect(() => {
    if (pendingConversion) conversionAppliedRef.current = false;
  }, [pendingConversion]);

  useEffect(() => {
    if (!pendingConversion || conversionAppliedRef.current) return;
    if (!branches.length) return; // wait for branch data
    conversionAppliedRef.current = true;

    const targetName = String(pendingConversion.customerName || "").trim().toLowerCase();
    const targetContact = String(pendingConversion.customerContact || "").trim().toLowerCase();
    const matchedClient = clients.find(c => {
      if (pendingConversion.customerPublicId && String(c.publicId || c.id || "") === String(pendingConversion.customerPublicId)) {
        return true;
      }
      const fullName = `${c.firstName || ""} ${c.lastName || ""}`.trim().toLowerCase();
      const mobile = String(c.mobile || "").toLowerCase();
      return (targetName && fullName === targetName) || (targetContact && mobile === targetContact);
    });
    const resolvedBranchPublicId = String(
      pendingConversion.branchPublicId
      || matchedClient?.branchPublicId
      || defaultAllowedBranchPublicId
      || branches[0]?.publicId
      || ""
    );

    const lineItems = (pendingConversion.services || []).map(item => ({
      lineId: createLineId(),
      servicePublicId: item.servicePublicId || "",
      serviceName: item.serviceName || "",
      servicePrice: Number(item.servicePrice || 0),
      lineDiscountValue: Number(item.lineDiscountAmount || 0),
      memberPublicId: item.memberPublicId || "",
      memberName: item.memberName || ""
    }));

    setEditingSaleRef("");
    setForm({
      ...defaultForm,
      saleDate: pendingConversion.saleDate || new Date().toISOString().slice(0, 10),
      branchPublicId: resolvedBranchPublicId,
      customerPublicId: matchedClient?.publicId || "",
      lineItems,
      note: pendingConversion.sourceRef ? `Converted from appointment ${pendingConversion.sourceRef}` : ""
    });
    setManualCustomerName(matchedClient ? getClientDisplayName(matchedClient) : (pendingConversion.customerName || ""));
    setManualCustomerContact(matchedClient?.mobile || pendingConversion.customerContact || "");
    setManualCustomerBirthday(matchedClient?.birthday || "");
    setLineDraft({ ...defaultLineDraft });
    setServiceSearchTerm("");
    setServiceFocused(false);
    setPanelReadOnly(false);
    conversionSourceRef.current = pendingConversion.sourceRef || null;
    setPanelOpen(true);
    if (onConversionDone) onConversionDone();
  }, [pendingConversion, clients, branches, defaultAllowedBranchPublicId]); // eslint-disable-line react-hooks/exhaustive-deps

  const branchScopedClients = useMemo(() => {
    const selectedBranchPublicId = String(form.branchPublicId || "").trim();
    if (!selectedBranchPublicId) return [];
    return clients.filter(client => String(client.branchPublicId || "") === selectedBranchPublicId);
  }, [clients, form.branchPublicId]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = new Set([String(currentYear - 1), String(currentYear), String(currentYear + 1)]);
    sales.forEach(sale => {
      const saleYear = String(sale?.saleDate || "").slice(0, 4);
      if (saleYear) years.add(saleYear);
    });
    return Array.from(years).sort((left, right) => Number(right) - Number(left));
  }, [sales]);

  const filteredClients = useMemo(() => {
    const query = String(manualCustomerName || "").trim().toLowerCase();
    if (!query) return [];
    return branchScopedClients.filter(client => {
      const fullName = getClientDisplayName(client).toLowerCase();
      const mobile = String(client.mobile || "").toLowerCase();
      return fullName.includes(query) || mobile.includes(query);
    }).slice(0, 8);
  }, [branchScopedClients, manualCustomerName]);

  const filteredServices = useMemo(() => {
    const query = String(serviceSearchTerm || "").trim().toLowerCase();
    if (!query) return services.slice(0, 12);
    return services.filter(service => {
      const serviceName = String(service?.name || "").toLowerCase();
      const servicePrice = String(Number(service?.price || 0).toFixed(2));
      return serviceName.includes(query) || servicePrice.includes(query);
    }).slice(0, 12);
  }, [services, serviceSearchTerm]);

  const filteredSales = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sales.filter(sale => (
      (() => {
        const saleDateKey = String(sale.saleDate || "").slice(0, 10);
        const saleYear = saleDateKey.slice(0, 4);
        const saleMonth = saleDateKey.slice(5, 7);
        const matchesSearch = !q || (
          (sale.invoiceNo || "").toLowerCase().includes(q) ||
          (sale.customerName || "").toLowerCase().includes(q) ||
          (sale.paymentMethod || "").toLowerCase().includes(q) ||
          saleDateKey.toLowerCase().includes(q)
        );
        const matchesDate = !dateFilter || saleDateKey === dateFilter;
        const matchesMonth = !monthFilter || saleMonth === String(monthFilter).padStart(2, "0");
        const matchesYear = !yearFilter || saleYear === String(yearFilter);
        const matchesPayment = !paymentFilter || String(sale.paymentMethod || "") === paymentFilter;
        return matchesSearch && matchesDate && matchesMonth && matchesYear && matchesPayment;
      })()
    ));
  }, [sales, search, dateFilter, monthFilter, yearFilter, paymentFilter]);

  useEffect(() => { setSalesPage(1); }, [filteredSales]);

  const pagedSales = useMemo(
    () => filteredSales.slice((salesPage - 1) * salesPageSize, salesPage * salesPageSize),
    [filteredSales, salesPage, salesPageSize]
  );

  const branchLabelById = useMemo(() => {
    const map = new Map();
    branches.forEach(branch => {
      const id = String(branch.publicId || "");
      if (!id) return;
      map.set(id, `${branch.branchCode || ""} - ${branch.branchName || "Branch"}`.trim());
    });
    return map;
  }, [branches]);

  const subtotal = useMemo(() => {
    return form.lineItems.reduce((sum, item) => sum + Number(item.servicePrice || 0), 0);
  }, [form.lineItems]);

  const lineDiscountAmount = useMemo(() => {
    return form.lineItems.reduce((sum, item) => sum + getLineDiscountValue(item), 0);
  }, [form.lineItems]);

  const discountAmount = useMemo(() => {
    return Math.min(lineDiscountAmount, subtotal);
  }, [lineDiscountAmount, subtotal]);

  const totalAmount = useMemo(() => {
    return Math.max(0, subtotal - discountAmount);
  }, [subtotal, discountAmount]);

  const vatAmount = useMemo(() => {
    const vat = Number(form.vatPercent || 0);
    if (vat <= 0) return 0;
    // Service price is VAT-inclusive; extract VAT part from the discounted total.
    return totalAmount - (totalAmount / (1 + vat / 100));
  }, [form.vatPercent, totalAmount]);

  const netBeforeVat = useMemo(() => {
    return Math.max(0, totalAmount - vatAmount);
  }, [totalAmount, vatAmount]);

  const listTotalSales = useMemo(() =>
    filteredSales.reduce((sum, s) => sum + Number(s.totalAmount || 0), 0)
  , [filteredSales]);

  const listTotalDiscount = useMemo(() =>
    filteredSales.reduce((sum, s) => sum + Number(s.discountAmount || 0), 0)
  , [filteredSales]);

  const listTotalVat = useMemo(() =>
    filteredSales.reduce((sum, s) => sum + Number(s.vatAmount || 0), 0)
  , [filteredSales]);

  const hasActiveFilters = useMemo(() => {
    return Boolean(search || dateFilter || monthFilter || yearFilter || paymentFilter);
  }, [search, dateFilter, monthFilter, yearFilter, paymentFilter]);

  const handleDateFilterChange = event => {
    const nextDate = String(event.target.value || "");
    setDateFilter(nextDate);

    const parsed = parseDateFilterKey(nextDate);
    if (!parsed) {
      setMonthFilter("");
      setYearFilter("");
      return;
    }

    const syncedMonth = String(parsed.month);
    const syncedYear = String(parsed.year);
    setMonthFilter(prev => (prev === syncedMonth ? prev : syncedMonth));
    setYearFilter(prev => (prev === syncedYear ? prev : syncedYear));
  };

  const syncDateFromMonthYear = (nextMonth, nextYear) => {
    const normalizedMonth = String(nextMonth || "").trim();
    const normalizedYear = String(nextYear || "").trim();
    if (!normalizedMonth || !normalizedYear) {
      setDateFilter("");
      return;
    }

    const baseDate = parseDateFilterKey(dateFilter) || parseDateFilterKey(getTodayDateKey());
    const preferredDay = baseDate?.day || 1;
    const nextDate = buildDateFilterKey(normalizedYear, normalizedMonth, preferredDay);
    setDateFilter(prev => (prev === nextDate ? prev : nextDate));
  };

  const handleMonthFilterChange = event => {
    const nextMonth = String(event.target.value || "");
    setMonthFilter(nextMonth);

    if (!nextMonth) {
      setDateFilter("");
      return;
    }

    const parsedDate = parseDateFilterKey(dateFilter);
    const resolvedYear = String(yearFilter || parsedDate?.year || getCurrentYearValue());
    if (!yearFilter) {
      setYearFilter(resolvedYear);
    }
    syncDateFromMonthYear(nextMonth, resolvedYear);
  };

  const handleYearFilterChange = event => {
    const nextYear = String(event.target.value || "");
    setYearFilter(nextYear);

    if (!nextYear) {
      setDateFilter("");
      return;
    }

    const parsedDate = parseDateFilterKey(dateFilter);
    const resolvedMonth = String(monthFilter || parsedDate?.month || getCurrentMonthValue());
    if (!monthFilter) {
      setMonthFilter(resolvedMonth);
    }
    syncDateFromMonthYear(resolvedMonth, nextYear);
  };

  const clearSalesFilters = () => {
    setSearch("");
    setDateFilter("");
    setMonthFilter("");
    setYearFilter("");
    setPaymentFilter("");
  };

  const exportSalesToExcel = () => {
    if (filteredSales.length === 0) {
      alert("No sales available for export.");
      return;
    }

    const escapeCell = value => String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

    const headers = [
      "No.", "Invoice", "Date", "Branch", "Customer", "Services",
      "Subtotal", "Discount", "VAT Included", "Total", "Payment", "Notes"
    ];

    const rows = filteredSales.map((sale, index) => ([
      index + 1,
      sale.invoiceNo || "-",
      String(sale.saleDate || "").slice(0, 10) || "-",
      branchLabelById.get(String(sale.branchPublicId || "")) || sale.branchPublicId || "-",
      sale.customerName || "-",
      Array.isArray(sale.services)
        ? sale.services.map(item => `${item.name || "Service"}${item.memberName ? ` (${item.memberName})` : ""}`).join(", ")
        : "-",
      Number(sale.subtotal || 0).toFixed(2),
      Number(sale.discountAmount || 0).toFixed(2),
      Number(sale.vatAmount || 0).toFixed(2),
      Number(sale.totalAmount || 0).toFixed(2),
      sale.paymentMethod || "-",
      sale.note || ""
    ]));

    const tableHtml = `
      <table>
        <thead>
          <tr>${headers.map(header => `<th>${escapeCell(header)}</th>`).join("")}</tr>
        </thead>
        <tbody>
          ${rows.map(columns => `<tr>${columns.map(cell => `<td>${escapeCell(cell)}</td>`).join("")}</tr>`).join("")}
        </tbody>
      </table>
    `;

    const workbookHtml = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head>
          <meta charset="utf-8" />
          <style>
            table { border-collapse: collapse; font-family: Arial, sans-serif; }
            th, td { border: 1px solid #c7c7c7; padding: 6px 8px; text-align: left; }
            th { background: #e8ecef; font-weight: 700; }
          </style>
        </head>
        <body>${tableHtml}</body>
      </html>
    `;

    const blob = new Blob([workbookHtml], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    const dateSuffix = new Date().toISOString().slice(0, 10);
    link.download = `sales-export-${dateSuffix}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === "branchPublicId") {
      setForm(prev => ({ ...prev, branchPublicId: value, customerPublicId: "" }));
      setManualCustomerName("");
      setManualCustomerContact("");
      setManualCustomerBirthday("");
      setCustomerFocused(false);
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomerNameChange = e => {
    setManualCustomerName(e.target.value);
    setForm(prev => ({ ...prev, customerPublicId: "" }));
  };

  const handleCustomerContactChange = e => {
    setManualCustomerContact(e.target.value);
    setForm(prev => ({ ...prev, customerPublicId: "" }));
  };

  const handleCustomerBirthdayChange = e => {
    setManualCustomerBirthday(e.target.value);
  };

  const selectCustomer = client => {
    setForm(prev => ({ ...prev, customerPublicId: client.publicId || client.id || "" }));
    setManualCustomerName(getClientDisplayName(client));
    setManualCustomerContact(client.mobile || "");
    setManualCustomerBirthday(client.birthday || "");
    setCustomerFocused(false);
  };

  const selectService = service => {
    const selectedPublicId = String(service?.publicId || service?.id || "");
    setLineDraft(prev => ({
      ...prev,
      servicePublicId: selectedPublicId,
      servicePrice: String(Number(service?.price || 0)),
      lineDiscountValue: ""
    }));
    setServiceSearchTerm(`${service?.name || "Service"} - AED ${Number(service?.price || 0).toFixed(2)}`);
    setServiceFocused(false);
  };

  const addLineItem = () => {
    const service = services.find(s => String(s.publicId || "") === String(lineDraft.servicePublicId || ""));
    const member = teamMembers.find(m => String(m.publicId || "") === String(lineDraft.memberPublicId || ""));

    if (!service) {
      alert("Select a service.");
      return;
    }
    if (!member) {
      alert("Select team member for this service.");
      return;
    }

    const enteredPrice = Number(lineDraft.servicePrice || service.price || 0);
    if (Number.isNaN(enteredPrice) || enteredPrice < 0) {
      alert("Enter a valid service price.");
      return;
    }

    const enteredLineDiscount = Number(lineDraft.lineDiscountValue || 0);
    if (Number.isNaN(enteredLineDiscount) || enteredLineDiscount < 0) {
      alert("Enter a valid line discount.");
      return;
    }
    if (enteredLineDiscount > enteredPrice) {
      alert("Line discount cannot exceed service price.");
      return;
    }

    const line = {
      lineId: createLineId(),
      servicePublicId: service.publicId,
      serviceName: service.name,
      servicePrice: enteredPrice,
      lineDiscountValue: enteredLineDiscount,
      memberPublicId: member.publicId,
      memberName: `${member.firstName || ""} ${member.lastName || ""}`.trim() || "Team Member"
    };

    setForm(prev => ({ ...prev, lineItems: [...prev.lineItems, line] }));
    setLineDraft(prev => ({
      ...defaultLineDraft,
      memberPublicId: prev.memberPublicId
    }));
    setServiceSearchTerm("");
    setServiceFocused(false);
  };

  const removeLineItem = lineId => {
    setForm(prev => ({ ...prev, lineItems: prev.lineItems.filter(item => item.lineId !== lineId) }));
  };

  const updateLineItemPrice = (lineId, value) => {
    const price = Number(value);
    if (Number.isNaN(price) || price < 0) return;
    setForm(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => (
        item.lineId === lineId
          ? { ...item, servicePrice: price, lineDiscountValue: Math.min(getLineDiscountValue(item), price) }
          : item
      ))
    }));
  };

  const updateLineItemDiscount = (lineId, value) => {
    const discount = Number(value);
    if (Number.isNaN(discount) || discount < 0) return;
    setForm(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => (
        item.lineId === lineId
          ? { ...item, lineDiscountValue: Math.min(discount, Number(item.servicePrice || 0)) }
          : item
      ))
    }));
  };

  const handleLineDraftPriceChange = e => {
    const value = e.target.value;
    if (value === "") {
      setLineDraft(prev => ({ ...prev, servicePrice: "" }));
      return;
    }
    const numeric = Number(value);
    if (Number.isNaN(numeric) || numeric < 0) return;
    setLineDraft(prev => ({ ...prev, servicePrice: value }));
  };

  const handleLineDraftDiscountChange = e => {
    const value = e.target.value;
    if (value === "") {
      setLineDraft(prev => ({ ...prev, lineDiscountValue: "" }));
      return;
    }
    const numeric = Number(value);
    if (Number.isNaN(numeric) || numeric < 0) return;
    setLineDraft(prev => ({ ...prev, lineDiscountValue: value }));
  };

  const normalizeLineDraftPrice = () => {
    const numeric = Number(lineDraft.servicePrice || 0);
    if (Number.isNaN(numeric) || numeric < 0) return;
    setLineDraft(prev => ({ ...prev, servicePrice: numeric.toFixed(2) }));
  };

  const normalizeLineDraftDiscount = () => {
    const price = Math.max(0, Number(lineDraft.servicePrice || 0));
    const discount = Math.max(0, Number(lineDraft.lineDiscountValue || 0));
    const normalized = Math.min(discount, price);
    setLineDraft(prev => ({ ...prev, lineDiscountValue: normalized.toFixed(2) }));
  };

  const submitSale = async e => {
    e.preventDefault();
    if (submitInFlightRef.current || isSaving) {
      return;
    }

    submitInFlightRef.current = true;
    setIsSaving(true);

    const releaseSubmitLock = () => {
      submitInFlightRef.current = false;
      setIsSaving(false);
    };

    const enteredName = String(manualCustomerName || "").trim();
    const enteredContact = String(manualCustomerContact || "").trim();
    if (!enteredName || !enteredContact) {
      alert("Please enter customer name and contact.");
      releaseSubmitLock();
      return;
    }
    if (!form.branchPublicId) {
      alert("Please select a branch.");
      releaseSubmitLock();
      return;
    }

    let customer = clients.find(c => String(c.publicId || c.id || "") === String(form.customerPublicId || "")) || null;
    const enteredContactNormalized = normalizeMobile(enteredContact);
    const enteredBirthday = String(manualCustomerBirthday || "").trim();

    if (!customer) {
      customer = clients.find(c => normalizeMobile(c.mobile) === enteredContactNormalized) || null;
    }

    if (!customer) {
      const { firstName, lastName } = splitCustomerName(enteredName);
      if (!firstName) {
        alert("Please enter a valid customer name.");
        releaseSubmitLock();
        return;
      }
      try {
        const createClientRes = await apiFetch("/api/clients", {
          method: "POST",
          body: JSON.stringify({
            firstName,
            lastName,
            mobile: enteredContact,
            birthday: enteredBirthday || null,
            branchPublicId: form.branchPublicId
          })
        });

        if (createClientRes.status === 409) {
          const refreshedClientsRes = await apiFetch("/api/clients");
          const refreshedClients = await refreshedClientsRes.json();
          const normalizedList = Array.isArray(refreshedClients) ? refreshedClients : [];
          setClients(normalizedList);
          customer = normalizedList.find(c => normalizeMobile(c.mobile) === enteredContactNormalized) || null;
          if (!customer) {
            alert("Customer already exists with this contact. Please select from the list.");
            releaseSubmitLock();
            return;
          }
        } else if (!createClientRes.ok) {
          const errorBody = await createClientRes.json().catch(() => ({}));
          alert(errorBody.error || "Could not create customer");
          releaseSubmitLock();
          return;
        } else {
          const createdClient = await createClientRes.json();
          customer = createdClient;
          setClients(prev => {
            const exists = prev.some(item => String(item.publicId || item.id || "") === String(createdClient.publicId || createdClient.id || ""));
            return exists ? prev : [createdClient, ...prev];
          });
        }
      } catch (createErr) {
        console.error("Failed to create client from sales", createErr);
        alert("Could not create customer");
        releaseSubmitLock();
        return;
      }
    }

    if (!customer) {
      alert("Please select a customer.");
      releaseSubmitLock();
      return;
    }
    setManualCustomerBirthday(customer.birthday || enteredBirthday || "");
    if (String(customer.branchPublicId || "") !== String(form.branchPublicId || "")) {
      alert("Selected customer belongs to a different branch.");
      releaseSubmitLock();
      return;
    }
    setForm(prev => ({ ...prev, customerPublicId: customer.publicId || customer.id || "" }));
    if (form.lineItems.length === 0) {
      alert("Please add at least one service line.");
      releaseSubmitLock();
      return;
    }

    const payload = {
      invoiceNo: form.invoiceNo || undefined,
      saleDate: form.saleDate,
      branchPublicId: form.branchPublicId,
      customerPublicId: customer.publicId || customer.id || undefined,
      customerName: `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || enteredName,
      services: form.lineItems.map(item => ({
        servicePublicId: item.servicePublicId,
        name: item.serviceName,
        price: Number(item.servicePrice || 0),
        lineDiscountAmount: getLineDiscountValue(item),
        lineTotal: getLineTotal(item),
        memberPublicId: item.memberPublicId,
        memberName: item.memberName
      })),
      subtotal,
      discountAmount,
      vatPercent: Number(form.vatPercent || 0),
      vatAmount,
      totalAmount,
      paymentMethod: form.paymentMethod,
      note: form.note
    };

    const invoiceLabel = form.invoiceNo ? ` ${form.invoiceNo}` : "";
    const confirmMessage = `Confirm save invoice${invoiceLabel} with total value AED ${Number(totalAmount || 0).toFixed(2)}?`;
    const shouldSave = window.confirm(confirmMessage);
    if (!shouldSave) {
      releaseSubmitLock();
      return;
    }

    try {
      const endpoint = isEditing
        ? `/api/sales/${encodeURIComponent(editingSaleRef)}`
        : "/api/sales";

      const res = await apiFetch(endpoint, {
        method: isEditing ? "PUT" : "POST",
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errorPayload = await res.json().catch(() => null);
        throw new Error(errorPayload?.error || `Server error ${res.status}`);
      }
      const saved = await res.json();
      if (isEditing) {
        setSales(prev => prev.map(item => (getSaleRef(item) === editingSaleRef ? saved : item)));
      } else {
        setSales(prev => [saved, ...prev]);
        if (conversionSourceRef.current) {
          const apptRef = conversionSourceRef.current;
          const savedSaleRef = String(saved.publicId || saved.id || "");
          const convertRes = await apiFetch(`/api/work/${encodeURIComponent(apptRef)}/convert`, {
            method: "PATCH",
            body: JSON.stringify({ saleRef: savedSaleRef })
          });
          if (convertRes.ok) {
            const updatedAppointment = await convertRes.json();
            onAppointmentConverted?.(updatedAppointment);
          } else {
            console.warn("Could not mark appointment as converted", convertRes.status);
          }
        }
      }
      const shouldPrint = window.confirm("Invoice saved successfully. Do you want to print it now?");
      closePanel();
      if (shouldPrint) {
        printInvoice(saved);
      }
    } catch (err) {
      console.error("Failed to save sale", err);
      alert(`Could not save invoice${err?.message ? `: ${err.message}` : ""}`);
      releaseSubmitLock();
    }
  };

  const deleteSale = async sale => {
    const ref = getSaleRef(sale);
    if (!ref) return;
    const ok = window.confirm(`Delete invoice ${sale.invoiceNo || ""}? This action cannot be undone.`);
    if (!ok) return;
    try {
      const res = await apiFetch(`/api/sales/${encodeURIComponent(ref)}`, { method: "DELETE" });
      if (!res.ok) {
        const errorPayload = await res.json().catch(() => null);
        throw new Error(errorPayload?.error || `Server error ${res.status}`);
      }
      setSales(prev => prev.filter(item => getSaleRef(item) !== ref));
    } catch (err) {
      console.error("Failed to delete sale", err);
      alert(`Could not delete invoice${err?.message ? `: ${err.message}` : ""}`);
    }
  };

  const printInvoice = sale => {
    const services = Array.isArray(sale.services) ? sale.services : [];
    const vatPercent = Number(sale.vatPercent || 5);
    const subtotal = Number(sale.subtotal || 0);
    const discount = Number(sale.discountAmount || 0);
    const total = Number(sale.totalAmount || 0);
    const vatIncluded = Number(sale.vatAmount || 0);
    const taxable = Math.max(0, total - vatIncluded);
    const hasVat = vatPercent > 0 && vatIncluded > 0;
    const invoiceTitle = hasVat ? "TAX INVOICE" : "SALES INVOICE";

    const branch = branches.find(item => String(item.publicId || "") === String(sale.branchPublicId || "")) || null;
    const branchLabel = branchLabelById.get(String(sale.branchPublicId || "")) || "-";
    const registeredName = branch?.registeredName || branch?.branchName || "-";
    const printAddress = branch?.address || COMPANY_INFO.address;
    const printTrn = branch?.trnId || COMPANY_INFO.trn;
    const printPhone = branch?.contactNumber || COMPANY_INFO.phone;
    const serviceRows = services.length
      ? services.map(item => {
          const linePrice = Number(item.price || 0);
          const lineDiscount = Math.max(0, Number(item.lineDiscountAmount || 0));
          const lineTotal = Math.max(0, Number(item.lineTotal != null ? item.lineTotal : linePrice - lineDiscount));
          const lineTaxable = hasVat ? (lineTotal / (1 + vatPercent / 100)) : lineTotal;
          const lineVat = hasVat ? (lineTotal - lineTaxable) : 0;
          return `
            <div class="line">
              <div class="line-title">${escapeHtml(item.name || "Service")}</div>
              <div class="line-row"><span>Price</span><span>AED ${linePrice.toFixed(2)}</span></div>
              <div class="line-row"><span>Discount</span><span>AED ${lineDiscount.toFixed(2)}</span></div>
              <div class="line-row"><span>Taxable</span><span>AED ${lineTaxable.toFixed(2)}</span></div>
              ${hasVat ? `<div class="line-row"><span>VAT ${vatPercent.toFixed(2)}%</span><span>AED ${lineVat.toFixed(2)}</span></div>` : ""}
              <div class="line-row strong"><span>Line Total</span><span>AED ${lineTotal.toFixed(2)}</span></div>
            </div>
          `;
        }).join("")
      : `<div class="line"><div class="line-title">No line items</div></div>`;

    const html = `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <title>${invoiceTitle} ${sale.invoiceNo || ""}</title>
        <style>
          @page { size: 72mm auto; margin: 0; }
          * { box-sizing: border-box; }
          html, body {
            margin: 0;
            padding: 0;
            width: 72mm;
            font-family: "Consolas", "Lucida Console", "Courier New", monospace;
            font-size: 10px;
            font-weight: 500;
            letter-spacing: 0;
            color: #000;
            background: #fff;
            text-rendering: optimizeLegibility;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .receipt {
            width: 72mm;
            padding: 1.5mm;
          }
          .center { text-align: center; }
          .title {
            font-size: 14px;
            font-weight: 700;
            letter-spacing: 0.4px;
            margin-bottom: 2px;
          }
          .company {
            font-size: 11px;
            font-weight: 700;
            line-height: 1.25;
          }
          .small {
            font-size: 10px;
            line-height: 1.25;
            word-break: break-word;
          }
          .rule {
            border-top: 1px dashed #000;
            margin: 5px 0;
          }
          .meta-row, .sum-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 8px;
            font-size: 10px;
            line-height: 1.3;
            margin: 2px 0;
          }
          .meta-row span:first-child,
          .sum-row span:first-child {
            max-width: 34mm;
          }
          .meta-row span:last-child,
          .sum-row span:last-child {
            text-align: right;
            white-space: nowrap;
          }
          .section-title {
            font-size: 10px;
            font-weight: 700;
            margin-bottom: 3px;
          }
          .line {
            border-bottom: 1px dashed #000;
            padding: 4px 0;
          }
          .line:last-child {
            border-bottom: 0;
          }
          .line-title {
            font-weight: 700;
            font-size: 10px;
            line-height: 1.25;
            margin-bottom: 1px;
            word-break: break-word;
          }
          .line-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 8px;
            font-size: 10px;
            line-height: 1.25;
          }
          .line-row span:last-child {
            white-space: nowrap;
            text-align: right;
          }
          .strong { font-weight: 700; }
          .sum-row.grand {
            border-top: 1px solid #000;
            border-bottom: 1px solid #000;
            padding: 3px 0;
            margin-top: 4px;
            font-size: 10.5px;
            font-weight: 700;
          }
          .note {
            margin-top: 4px;
            font-size: 10px;
            line-height: 1.3;
            word-break: break-word;
          }
          .footer {
            margin-top: 6px;
            font-size: 10px;
            line-height: 1.3;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="center">
            <div class="title">${invoiceTitle}</div>
            <div class="company">${escapeHtml(registeredName)}</div>
            <div class="small">${escapeHtml(printAddress)}</div>
            <div class="small">TRN: ${escapeHtml(printTrn)}</div>
            <div class="small">Phone: ${escapeHtml(printPhone)}</div>
          </div>

          <div class="rule"></div>
          <div class="meta-row"><span>Invoice No</span><span>${escapeHtml(sale.invoiceNo || "-")}</span></div>
          <div class="meta-row"><span>Date</span><span>${escapeHtml(sale.saleDate || "-")}</span></div>
          <div class="meta-row"><span>Branch</span><span>${escapeHtml(branchLabel)}</span></div>
          <div class="meta-row"><span>Customer</span><span>${escapeHtml(sale.customerName || "Walk-in")}</span></div>
          <div class="meta-row"><span>Payment</span><span>${escapeHtml(sale.paymentMethod || "-")}</span></div>

          <div class="rule"></div>
          <div class="section-title">Services</div>
          ${serviceRows}

          <div class="rule"></div>
          <div class="sum-row"><span>Subtotal (Incl. VAT)</span><span>AED ${subtotal.toFixed(2)}</span></div>
          <div class="sum-row"><span>Discount</span><span>AED ${discount.toFixed(2)}</span></div>
          <div class="sum-row"><span>Taxable Amount</span><span>AED ${taxable.toFixed(2)}</span></div>
          ${hasVat ? `<div class="sum-row"><span>VAT ${vatPercent.toFixed(2)}%</span><span>AED ${vatIncluded.toFixed(2)}</span></div>` : ""}
          <div class="sum-row grand"><span>Total</span><span>AED ${total.toFixed(2)}</span></div>

          ${sale.note ? `<div class="note"><strong>Note:</strong> ${escapeHtml(sale.note)}</div>` : ""}

          <div class="footer">
            ${hasVat ? "VAT calculated as per UAE VAT regulations.<br />" : ""}
            Thank you for your visit.
          </div>
        </div>
        <script>
          (function () {
            var closeAndReturn = function () {
              try {
                if (window.opener && !window.opener.closed) {
                  window.opener.focus();
                }
              } catch (e) {
                // Ignore cross-window focus errors
              }
              window.close();
            };

            window.onafterprint = closeAndReturn;
            setTimeout(closeAndReturn, 2000);
          })();
        ${"</script>"}
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=360,height=900");
    if (!printWindow) {
      alert("Unable to open print window. Please allow pop-ups.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    let cleanedUp = false;
    const cleanupPrintWindow = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      try {
        if (!printWindow.closed) {
          printWindow.close();
        }
      } catch (err) {
        // Ignore close errors on some mobile browsers
      }
      try {
        window.focus();
      } catch (err) {
        // Ignore focus errors
      }
    };

    const triggerPrint = () => {
      try {
        printWindow.onafterprint = cleanupPrintWindow;
        printWindow.focus();
        printWindow.print();
      } catch (err) {
        console.error("Print failed", err);
        cleanupPrintWindow();
      }

      // Mobile fallback: force close blank popup after preview/print returns.
      setTimeout(cleanupPrintWindow, 1800);
      setTimeout(cleanupPrintWindow, 4500);
    };

    if (printWindow.document.readyState === "complete") {
      triggerPrint();
    } else {
      printWindow.onload = triggerPrint;
      setTimeout(triggerPrint, 700);
    }

    // Keep focus on app once the print window closes.
    const watcher = setInterval(() => {
      if (printWindow.closed) {
        clearInterval(watcher);
        window.focus();
      }
    }, 500);

    // Safety net: stop watcher if browser keeps popup alive but hidden.
    setTimeout(() => clearInterval(watcher), 7000);
  };

  return (
    <div className="sales-page page main-content">
      <div className="sales-header">
        <div>
          <h1>Sales</h1>
          <p>Manage customer invoices, payment methods, and branch-wise daily sales records.</p>
        </div>
        <div className="sales-header-actions">
          <button type="button" className="sales-toolbar-btn sales-export-btn" onClick={exportSalesToExcel}>Export Excel</button>
          <button
            className="open-panel-btn"
            onClick={openPanel}
            style={canDo(currentUser, "sales", "add") ? {} : { display: "none" }}
          >
            Add Sale
          </button>
        </div>
      </div>

      <div className="filter-section sales-filter-section">
        <select className="filter-select sales-branch-filter" value={branchFilter} onChange={e => setBranchFilter(e.target.value)}>
          {branches.length !== 1 && <option value="">All branches</option>}
          {branches.map(branch => (
            <option key={branch.publicId || branch.id} value={branch.publicId || ""}>
              {`${branch.branchCode || ""} - ${branch.branchName || "Branch"}`}
            </option>
          ))}
        </select>
        <input
          className="filter-input sales-search-field"
          placeholder="Search by invoice, customer, date, payment"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <input
          className="filter-input sales-date-filter"
          type="date"
          value={dateFilter}
          onChange={handleDateFilterChange}
        />
        <select className="filter-select sales-month-filter" value={monthFilter} onChange={handleMonthFilterChange}>
          <option value="">All months</option>
          {monthLabels.map((label, index) => (
            <option key={label} value={String(index + 1)}>{label}</option>
          ))}
        </select>
        <select className="filter-select sales-year-filter" value={yearFilter} onChange={handleYearFilterChange}>
          <option value="">All years</option>
          {yearOptions.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <select className="filter-select sales-payment-filter" value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)}>
          <option value="">All payments</option>
          {paymentOptions.map(payment => (
            <option key={payment} value={payment}>{payment}</option>
          ))}
        </select>
        {hasActiveFilters && (
          <button type="button" className="clear-filter-btn" onClick={clearSalesFilters}>Clear Filters</button>
        )}
      </div>

      <div className="sales-list">
        {filteredSales.length === 0 ? (
          <p>No sales yet.</p>
        ) : (
          <table className="sales-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Date</th>
                <th>Branch</th>
                <th>Customer</th>
                <th>Services</th>
                <th className="num-cell">Subtotal</th>
                <th className="num-cell">Discount</th>
                <th className="num-cell">VAT Included</th>
                <th className="num-cell">Total</th>
                <th>Payment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedSales.map(sale => (
                <tr key={getSaleRef(sale)}>
                  <td data-label="Invoice">
                    <button
                      type="button"
                      className="invoice-link-btn"
                      onClick={() => openEditPanel(sale, { readOnly: !canEditSales })}
                      title={canEditSales ? "Open invoice" : "View invoice (read-only)"}
                    >
                      {sale.invoiceNo || "-"}
                    </button>
                  </td>
                  <td data-label="Date">{sale.saleDate || "-"}</td>
                  <td data-label="Branch">{branchLabelById.get(String(sale.branchPublicId || "")) || sale.branchPublicId || "-"}</td>
                  <td data-label="Customer">{sale.customerName || "-"}</td>
                  <td data-label="Services">
                    {Array.isArray(sale.services)
                      ? sale.services.map(s => `${s.name}${s.memberName ? ` (${s.memberName})` : ""}`).join(", ")
                      : "-"}
                  </td>
                  <td data-label="Subtotal" className="num-cell">{`AED ${Number(sale.subtotal || 0).toFixed(2)}`}</td>
                  <td data-label="Discount" className="num-cell">{`AED ${Number(sale.discountAmount || 0).toFixed(2)}`}</td>
                  <td data-label="VAT Included" className="num-cell">{`AED ${Number(sale.vatAmount || 0).toFixed(2)}`}</td>
                  <td data-label="Total" className="num-cell"><strong>{`AED ${Number(sale.totalAmount || 0).toFixed(2)}`}</strong></td>
                  <td data-label="Payment">{sale.paymentMethod || "-"}</td>
                  <td data-label="Actions" className="actions-cell">
                    <div className="invoice-actions">
                      <button
                        type="button"
                        className="icon-btn print-btn"
                        title="Print"
                        aria-label="Print invoice"
                        onClick={() => printInvoice(sale)}
                        style={canDo(currentUser, "sales", "print") ? {} : { display: "none" }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                          <path d="M7 8V4h10v4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M7 17H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M7 14h10v6H7z" fill="#fff" opacity="0.95"/>
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="icon-btn edit-btn"
                        title="Edit"
                        aria-label="Edit invoice"
                        onClick={() => openEditPanel(sale)}
                        style={canEditSales ? {} : { display: "none" }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="#fff" opacity="0.9"/>
                          <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="#fff" opacity="0.9"/>
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="icon-btn delete-btn"
                        title="Delete"
                        aria-label="Delete invoice"
                        onClick={() => deleteSale(sale)}
                        style={canDo(currentUser, "sales", "delete") ? {} : { display: "none" }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                          <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12z" fill="#fff" opacity="0.95"/>
                          <path d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="#fff" opacity="0.95"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="sales-table-totals">
              <tr>
                <td colSpan={5} className="totals-label">Totals ({filteredSales.length} record{filteredSales.length !== 1 ? "s" : ""})</td>
                <td data-label="Subtotal"></td>
                <td data-label="Discount Total" className="num-cell"><strong>{`AED ${listTotalDiscount.toFixed(2)}`}</strong></td>
                <td data-label="VAT Total" className="num-cell"><strong>{`AED ${listTotalVat.toFixed(2)}`}</strong></td>
                <td data-label="Sales Total" className="num-cell"><strong>{`AED ${listTotalSales.toFixed(2)}`}</strong></td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        )}
        <Paginator
          page={salesPage}
          pageSize={salesPageSize}
          total={filteredSales.length}
          onPageChange={setSalesPage}
          onPageSizeChange={size => { setSalesPageSize(size); setSalesPage(1); }}
        />
      </div>

      <SidePanel isOpen={panelOpen} onClose={closePanel} className="panel-wide">
        <div className="sales-form-container">
          <h2>
            {isEditing
              ? `${panelReadOnly ? "View" : "Edit"} Invoice ${form.invoiceNo ? `(${form.invoiceNo})` : ""}`
              : "Add Sale"}
          </h2>
          <form className="sales-form" onSubmit={panelReadOnly ? e => e.preventDefault() : submitSale}>
            <div className="form-section-title">Basic Sales Details</div>
            <div className="sales-grid two-col">
              <select name="branchPublicId" value={form.branchPublicId} onChange={handleChange} required disabled={panelReadOnly}>
                <option value="">Select branch</option>
                {branches.map(branch => (
                  <option key={branch.publicId || branch.id} value={branch.publicId || ""}>
                    {`${branch.branchCode || ""} - ${branch.branchName || "Branch"}`}
                  </option>
                ))}
              </select>
              <input type="date" name="saleDate" value={form.saleDate} onChange={handleChange} required disabled={panelReadOnly} />
            </div>
            <div className="customer-contact-row sales-customer-row">
              <div className="customer-field-wrapper">
                <input
                  type="text"
                  placeholder="Customer name (type name/mobile to search existing customers)"
                  value={manualCustomerName}
                  onChange={handleCustomerNameChange}
                  onFocus={() => {
                    if (!panelReadOnly) setCustomerFocused(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => setCustomerFocused(false), 120);
                  }}
                  required
                  autoComplete="off"
                  disabled={panelReadOnly}
                />
                {!panelReadOnly && customerFocused && String(manualCustomerName || "").trim() && (
                  <div className="customer-search-list">
                    {filteredClients.length === 0 ? (
                      <div className="service-search-empty">No customer found.</div>
                    ) : (
                      filteredClients.map(client => {
                        const id = client.publicId || String(client.id || `${client.firstName || ""}-${client.lastName || ""}-${client.mobile || ""}`);
                        return (
                          <button
                            key={id}
                            type="button"
                            className="customer-search-item"
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => selectCustomer(client)}
                          >
                            <span>{getClientDisplayName(client)}</span>
                            <small>{client.mobile || "-"}</small>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              <input
                type="text"
                placeholder="Customer contact"
                value={manualCustomerContact}
                onChange={handleCustomerContactChange}
                required
                disabled={panelReadOnly}
              />
              <input
                type="date"
                placeholder="Customer date of birth"
                value={manualCustomerBirthday}
                onChange={handleCustomerBirthdayChange}
                disabled={panelReadOnly}
              />
            </div>

            <div className="form-section-title">Services Done</div>
            <div className="sales-grid five-col service-entry-row">
              <div className="customer-field-wrapper service-field-wrapper">
                <input
                  type="text"
                  placeholder="Search service by name or price"
                  value={serviceSearchTerm}
                  onChange={e => {
                    const nextValue = e.target.value;
                    setServiceSearchTerm(nextValue);
                    setLineDraft(prev => ({ ...prev, servicePublicId: "" }));
                  }}
                  onFocus={() => {
                    if (!panelReadOnly) setServiceFocused(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => setServiceFocused(false), 120);
                  }}
                  autoComplete="off"
                  disabled={panelReadOnly}
                />
                {!panelReadOnly && serviceFocused && (
                  <div className="service-search-list">
                    {filteredServices.length === 0 ? (
                      <div className="service-search-empty">No service found.</div>
                    ) : (
                      filteredServices.map(service => {
                        const id = String(service.publicId || service.id || "");
                        return (
                          <button
                            key={id}
                            type="button"
                            className="service-search-item"
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => selectService(service)}
                          >
                            <span>{service.name || "Service"}</span>
                            <small>{`AED ${Number(service.price || 0).toFixed(2)}`}</small>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              <input
                className="line-draft-number-input"
                type="number"
                min="0"
                step="0.01"
                placeholder="Service price (AED)"
                value={lineDraft.servicePrice}
                disabled={panelReadOnly}
                onChange={handleLineDraftPriceChange}
                onBlur={normalizeLineDraftPrice}
              />
              <input
                className="line-draft-number-input"
                type="number"
                min="0"
                step="0.01"
                placeholder="Discount (AED, e.g. 10.00)"
                title="Optional line discount in AED"
                value={lineDraft.lineDiscountValue}
                disabled={panelReadOnly}
                onChange={handleLineDraftDiscountChange}
                onBlur={normalizeLineDraftDiscount}
              />
              <select
                value={lineDraft.memberPublicId}
                disabled={panelReadOnly}
                onChange={e => setLineDraft(prev => ({ ...prev, memberPublicId: e.target.value }))}
              >
                <option value="">Select member</option>
                {teamMembers.map(member => (
                  <option key={member.publicId || member.id} value={member.publicId || ""}>{`${member.firstName || ""} ${member.lastName || ""}`.trim()}</option>
                ))}
              </select>
              <button type="button" className="line-add-btn" onClick={addLineItem} disabled={panelReadOnly}>Add Service</button>
            </div>

            <div className="line-items-box">
              {form.lineItems.length === 0 ? (
                <p>No service lines added.</p>
              ) : (
                <table className="line-items-table">
                  <colgroup>
                    <col className="line-col-service" />
                    <col className="line-col-member" />
                    <col className="line-col-price" />
                    <col className="line-col-discount" />
                    <col className="line-col-total" />
                    <col className="line-col-action" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Team Member</th>
                      <th className="line-numeric-cell">Price</th>
                      <th className="line-numeric-cell">Discount</th>
                      <th className="line-numeric-cell">Total</th>
                      <th className="line-action-cell">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lineItems.map(item => (
                      <tr key={item.lineId}>
                        <td data-label="Service" className="line-service-cell">{item.serviceName}</td>
                        <td data-label="Team Member">{item.memberName}</td>
                        <td data-label="Price" className="line-numeric-cell">
                          <input
                            className="line-price-input"
                            type="number"
                            min="0"
                            step="0.01"
                            value={Number(item.servicePrice || 0).toFixed(2)}
                            disabled={panelReadOnly}
                            onChange={e => updateLineItemPrice(item.lineId, e.target.value)}
                          />
                        </td>
                        <td data-label="Line Discount" className="line-numeric-cell">
                          <input
                            className="line-price-input"
                            type="number"
                            min="0"
                            step="0.01"
                            value={getLineDiscountValue(item).toFixed(2)}
                            disabled={panelReadOnly}
                            onChange={e => updateLineItemDiscount(item.lineId, e.target.value)}
                          />
                        </td>
                        <td data-label="Line Total" className="line-numeric-cell">
                          <input
                            className="line-price-input"
                            type="number"
                            min="0"
                            step="0.01"
                            value={getLineTotal(item).toFixed(2)}
                            readOnly
                            disabled
                          />
                        </td>
                        <td data-label="Action" className="line-action-cell">
                          <button type="button" className="remove-line-btn" onClick={() => removeLineItem(item.lineId)} disabled={panelReadOnly}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="form-section-title">VAT and Payment (Included In Service Price)</div>
            <div className="sales-grid two-col">
              <input type="number" min="0" step="0.01" name="vatPercent" placeholder="VAT %" value={form.vatPercent} onChange={handleChange} disabled={panelReadOnly} />
              <select name="paymentMethod" value={form.paymentMethod} onChange={handleChange} disabled={panelReadOnly}>
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="Online">Online</option>
                <option value="Mixed">Mixed</option>
              </select>
            </div>

            <textarea name="note" placeholder="Notes" value={form.note} onChange={handleChange} disabled={panelReadOnly}></textarea>

            <div className="sale-summary-box">
              <div><span>Subtotal (VAT incl.)</span><strong>{`AED ${subtotal.toFixed(2)}`}</strong></div>
              <div><span>Service Discount</span><strong>{`AED ${lineDiscountAmount.toFixed(2)}`}</strong></div>
              <div><span>Discount</span><strong>{`AED ${discountAmount.toFixed(2)}`}</strong></div>
              <div><span>VAT Included</span><strong>{`AED ${vatAmount.toFixed(2)}`}</strong></div>
              <div><span>Net Before VAT</span><strong>{`AED ${netBeforeVat.toFixed(2)}`}</strong></div>
              <div className="sale-total"><span>Total (VAT incl.)</span><strong>{`AED ${totalAmount.toFixed(2)}`}</strong></div>
            </div>

            {isEditing && saleAudit && (saleAudit.createdBy || saleAudit.createdAt || saleAudit.updatedBy || saleAudit.updatedAt) && (
              <div className="audit-trail">
                {(saleAudit.createdBy || saleAudit.createdAt) && (
                  <span className="audit-item"><strong>Created by:</strong> {saleAudit.createdBy || "\u2014"}{saleAudit.createdAt ? ` \u00b7 ${new Date(saleAudit.createdAt).toLocaleString()}` : ""}</span>
                )}
                {(saleAudit.updatedBy || saleAudit.updatedAt) && (
                  <span className="audit-item"><strong>Updated by:</strong> {saleAudit.updatedBy || "\u2014"}{saleAudit.updatedAt ? ` \u00b7 ${new Date(saleAudit.updatedAt).toLocaleString()}` : ""}</span>
                )}
              </div>
            )}
            {!panelReadOnly && (
              <div className="sales-form-actions">
                <button type="submit" disabled={isSaving}>{isSaving ? "Saving..." : isEditing ? "Update Invoice" : "Save Sale"}</button>
              </div>
            )}
          </form>
        </div>
      </SidePanel>
    </div>
  );
};

export default Sales;
